import type { Schema } from '@/api/client'

/**
 * The GA4 Data API's single read scope, spelled as `oauth.SCOPE_ANALYTICS`.
 *
 * Hardcoded because the generated client carries no constant for it: `scopes` is
 * `list[str]` on the wire and OpenAPI has no enum to generate from. If the
 * consent screen's scope set ever changes, `web/src/pages/settings/ga4.test.tsx`
 * fails on the literal rather than the card quietly reporting every tenant as
 * having withheld a permission they granted.
 *
 * Its own file rather than `Ga4Card.tsx` because the row on `ConnectionsPanel`
 * reads the same answer, and a component file that also exports a function
 * breaks fast refresh.
 */
export const SCOPE_ANALYTICS = 'https://www.googleapis.com/auth/analytics.readonly'

type GoogleConnection = Schema<'SearchConsoleConnectionOut'>
type Site = Schema<'SiteListItemOut'>

/** A site is reading Analytics only when a property id is actually set on it. */
export function reads(site: Site): boolean {
  return (
    site.ga4_property_id !== null &&
    site.ga4_property_id !== undefined &&
    site.ga4_property_id !== ''
  )
}

/**
 * What the GA4 row and this card both say about the state — one answer, so
 * the list row above cannot read *Connected* while the card below reads *No
 * property chosen*. It did: the row keyed on the Google grant alone, and a
 * grant is not a property. Exported for the row; `settled` is the caller's
 * to decide because the two reads are the caller's.
 */
export function ga4State(
  data: GoogleConnection | null | undefined,
  listed: readonly Site[],
): 'reading' | 'no_property' | 'not_granted' | 'not_connected' {
  const connected = data?.connected === true
  const granted = data?.scopes?.includes(SCOPE_ANALYTICS) === true
  if (!connected) return 'not_connected'
  if (!granted) return 'not_granted'
  return listed.some(reads) ? 'reading' : 'no_property'
}
